<template lang="pug">
    .fixture-properties-panel(v-if="fixture")
        el-form(label-position="top" size="small")
            el-form-item(:label="$t('Label')")
                el-input(
                    v-model="localLabel"
                    :disabled="!canUpdate"
                    :maxlength="60"
                    @change="updateField('label', localLabel)"
                )
                .help(v-if="isLine") {{ $t('Shown above the divider, leave empty for a plain line.') }}

            el-form-item(v-if="isStairs" :label="$t('Direction')")
                el-radio-group(
                    v-model="localDirection"
                    size="small"
                    :disabled="!canUpdate"
                    @change="updateField('direction', localDirection)"
                )
                    el-radio-button(label="up") {{ $t('Upwards') }}
                    el-radio-button(label="down") {{ $t('Downwards') }}
                    el-radio-button(label="both") {{ $t('Both') }}

            el-form-item(v-if="showUpTarget" :label="upTargetLabel")
                el-select(
                    v-model="localTargetRoomId"
                    clearable
                    :disabled="!canUpdate"
                    :placeholder="$t('No area')"
                    @change="updateField('targetRoomId', localTargetRoomId || null)"
                )
                    el-option(
                        v-for="room in selectableRooms"
                        :key="room.id"
                        :label="room.name"
                        :value="room.id"
                    )
                .help(v-if="!showDownTarget") {{ $t('In view mode a click on the object opens this area.') }}

            el-form-item(v-if="showDownTarget" :label="$t('Target area downwards')")
                el-select(
                    v-model="localTargetRoomDownId"
                    clearable
                    :disabled="!canUpdate"
                    :placeholder="$t('No area')"
                    @change="updateField('targetRoomDownId', localTargetRoomDownId || null)"
                )
                    el-option(
                        v-for="room in selectableRooms"
                        :key="room.id"
                        :label="room.name"
                        :value="room.id"
                    )
                .help {{ $t('In view mode a click on the object opens this area.') }}

            el-form-item(:label="isLine ? $t('Length') + ' (px)' : $t('Width') + ' (px)'")
                el-slider(
                    v-model="localWidth"
                    :min="isLine ? 40 : 20"
                    :max="800"
                    :step="10"
                    :disabled="!canUpdate"
                    @change="updateField('width', localWidth)"
                )

            el-form-item(:label="isLine ? $t('Thickness') + ' (px)' : $t('Height') + ' (px)'")
                el-slider(
                    v-model="localHeight"
                    :min="isLine ? 2 : 20"
                    :max="isLine ? 16 : 600"
                    :step="isLine ? 1 : 10"
                    :disabled="!canUpdate"
                    @change="updateField('height', localHeight)"
                )

            el-form-item(:label="$t('Rotation') + ' (°)'")
                el-slider(
                    v-model="localRotation"
                    :min="0"
                    :max="359"
                    :step="5"
                    :disabled="!canUpdate"
                    @change="updateField('rotation', localRotation)"
                )

            el-divider

            .panel-actions
                el-button.panel-actions__delete(
                    v-if="canDelete"
                    size="small"
                    type="danger"
                    icon="el-icon-fa-trash-alt far"
                    @click="$emit('delete')"
                ) {{ $t('Delete object') }}

                el-button(
                    size="small"
                    type="primary"
                    icon="el-icon-fa-check fas"
                    :disabled="!canUpdate"
                    @click="save"
                ) {{ $t('Save') }}
</template>

<i18n>
{
    "de": {
        "Label": "Beschriftung",
        "Width": "Breite",
        "Height": "Höhe",
        "Length": "Länge",
        "Thickness": "Stärke",
        "Rotation": "Drehung",
        "Save": "Speichern",
        "Delete object": "Objekt löschen",
        "Shown above the divider, leave empty for a plain line.": "Wird über der Trennlinie angezeigt, leer lassen für eine reine Linie.",
        "Target area": "Zielbereich",
        "Target area upwards": "Zielbereich nach oben",
        "Target area downwards": "Zielbereich nach unten",
        "No area": "Kein Bereich",
        "Direction": "Richtung",
        "Upwards": "Nach oben",
        "Downwards": "Nach unten",
        "Both": "Beides",
        "In view mode a click on the object opens this area.": "Ein Klick auf das Objekt öffnet in der Ansicht diesen Bereich."
    }
}
</i18n>

<script>
export default {
    name: 'FixturePropertiesPanel',
    props: {
        fixture: {
            type: Object,
            required: true
        },
        rooms: {
            type: Array,
            default: function () { return []; }
        },
        canDelete: {
            type: Boolean,
            default: false
        },
        canUpdate: {
            type: Boolean,
            default: false
        }
    },
    data: function () {
        return {
            localLabel: '',
            localWidth: 160,
            localHeight: 60,
            localRotation: 0,
            localTargetRoomId: null,
            localTargetRoomDownId: null,
            localDirection: 'up'
        };
    },
    computed: {
        isLine: function () {
            return this.fixture.type === 'line';
        },
        isStairs: function () {
            return this.fixture.type === 'stairs' || this.fixture.type === 'spiral-stairs';
        },
        hasTargetRoom: function () {
            return this.isStairs || this.fixture.type === 'area-link';
        },
        showUpTarget: function () {
            return this.hasTargetRoom && (!this.isStairs || this.localDirection !== 'down');
        },
        showDownTarget: function () {
            return this.isStairs && (this.localDirection === 'down' || this.localDirection === 'both');
        },
        upTargetLabel: function () {
            return this.showDownTarget ? this.$t('Target area upwards') : this.$t('Target area');
        },
        selectableRooms: function () {
            return this.rooms.filter((room) => {
                return room.id !== this.fixture.roomId;
            });
        }
    },
    watch: {
        fixture: {
            immediate: true,
            handler: function (value) {
                if (!value) {
                    return;
                }

                this.localLabel = value.label;
                this.localWidth = value.width;
                this.localHeight = value.height;
                this.localRotation = value.rotation;
                this.localTargetRoomId = value.targetRoomId;
                this.localTargetRoomDownId = value.targetRoomDownId;
                this.localDirection = value.direction;
            }
        }
    },
    methods: {
        updateField: function (field, value) {
            if (!this.canUpdate) {
                return;
            }

            this.$emit('update', { [field]: value });
        },
        save: function () {
            if (!this.canUpdate) {
                return;
            }

            const data = {
                label: this.localLabel,
                width: this.localWidth,
                height: this.localHeight,
                rotation: this.localRotation
            };

            if (this.hasTargetRoom) {
                data.targetRoomId = this.showUpTarget ? (this.localTargetRoomId || null) : null;
            }

            if (this.isStairs) {
                data.direction = this.localDirection;
                data.targetRoomDownId = this.showDownTarget ? (this.localTargetRoomDownId || null) : null;
            }

            this.$emit('save', data);
        }
    }
};
</script>

<style lang="sass" scoped>
.fixture-properties-panel
    .help
        font-size: 0.75rem
        color: #909399
        line-height: 1.4

    .panel-actions
        display: flex
        justify-content: flex-end
        gap: 8px

        &__delete
            margin-right: auto
</style>
